class ProductPriceStarter extends HTMLElement {
  static get updateEventName() {
    return 'product-price-starter:update';
  }

  /**
   * Broadcasts an update for each `<product-price-starter>` instance to update itself.
   * @param {string|number} amount
   * @param {{ hidePrefix?: boolean }} [opts]
   */
  static dispatchUpdate(amount, opts) {
    const detail = { amount: amount };
    if (opts != null) detail.opts = opts;
    
    document.dispatchEvent(
      new CustomEvent(ProductPriceStarter.updateEventName, { detail: detail, bubbles: true })
    );
  }

  connectedCallback() {
    this.onBroadcast = this.onBroadcast.bind(this);
    document.addEventListener(ProductPriceStarter.updateEventName, this.onBroadcast);
  }

  disconnectedCallback() {
    document.removeEventListener(ProductPriceStarter.updateEventName, this.onBroadcast);
  }

  onBroadcast(e) {
    const detail = (e && e.detail) || {};
    const amount = detail.amount;
    let opts = detail.opts;

    if (opts == null) {
      opts = {};
      if (detail.hidePrefix !== undefined) opts.hidePrefix = detail.hidePrefix;
    }

    this.startingPriceUpdates(amount, opts);
  }

  // Checking if the user is choosing measurements options
  isChoosingMeasurementsOptions() {
    const drawer = document.querySelector('calculator-drawer');
    if (!drawer || !drawer.classList.contains('active')) return false;
    if (typeof window.getActiveTrigger !== 'function') return false;

    const windowTrigger = window.getActiveTrigger();
    if (!windowTrigger || !windowTrigger.dataset || !windowTrigger.dataset.target) return false;

    const target = windowTrigger.dataset.target;
    return target === 'Measurements' || target === 'Size';
  }

  /**
   * Applies the new `amount` to the UI.
   *
   * @param {string|number} amount - Current figure (starting amount from calculator).
   * @param {object} [opts]
   * @param {boolean} [opts.hidePrefix=true]
   */

  startingPriceUpdates(amount, opts) {
    const priceWrapper = this.querySelector('[data-set-starting-price-wrapper]');
    if (!priceWrapper) return;

    const priceEl = priceWrapper.querySelector('[data-set-starting-price]');
    if (!priceEl) return;

    const pricePrefix = priceWrapper.querySelector('[data-set-starting-price-prefix]');
    const currencySymbol = this.getAttribute('data-currency-symbol') || '';
    const wrapperType = priceWrapper.getAttribute('data-set-starting-price-wrapper') || '';
    const exposeDeltaToWindow = wrapperType === 'product-builder';

    opts = opts || {};
    const hidePrefix = opts.hidePrefix !== false;

    const raw = amount == null ? '' : String(amount).trim();
    const currentNum = parseFloat(raw.replace(/[^\d.-]/g, ''));
    const hasCurrent = !Number.isNaN(currentNum);

    const skipPlusMinusDiff = priceWrapper.getAttribute('data-set-starting-price-wrapper') === 'product-form' || this.isChoosingMeasurementsOptions();

    const baselineRaw = priceEl.getAttribute('data-set-initial-variant-price') || '';

    if (this._priceStarterBaselineRaw !== baselineRaw) {
      this._priceStarterBaselineRaw = baselineRaw;
      this._lastPriceStarterTotal = undefined;
    }

    const main = hasCurrent ? currentNum.toFixed(2) : raw;
    priceEl.setAttribute('data-set-starting-price', main);

    let line = main;
    let windowDeltaSuffix = '';

    if (skipPlusMinusDiff) {
      priceEl.setAttribute('data-set-price-plus', '0');
      priceEl.setAttribute('data-set-price-minus', '0');
    } else if (hasCurrent) {
      const prevTotal = Number.isFinite(this._lastPriceStarterTotal) ? this._lastPriceStarterTotal : null;
      const delta = prevTotal === null ? 0 : currentNum - prevTotal;
      this._lastPriceStarterTotal = currentNum;

      const abs = Math.abs(delta);

      if (prevTotal !== null) {
        // The price addition total so far (include zero deltas on selection change)
        const deltaSuffix = abs >= 0.005
          ? (delta > 0
            ? `(+${currencySymbol}${abs.toFixed(2)})`
            : `(-${currencySymbol}${abs.toFixed(2)})`)
          : `(${currencySymbol}0.00)`;

        line = `${main}`;
        windowDeltaSuffix = deltaSuffix;
      }

      priceEl.setAttribute('data-set-price-plus', delta > 0 ? abs.toFixed(2) : '0');
      priceEl.setAttribute('data-set-price-minus', delta < 0 ? abs.toFixed(2) : '0');
    } else {
      priceEl.setAttribute('data-set-price-plus', '0');
      priceEl.setAttribute('data-set-price-minus', '0');
    }

    if (typeof window !== 'undefined' && exposeDeltaToWindow) {
      window.productPriceStarterDeltaSuffix = windowDeltaSuffix; 
      document.dispatchEvent(
        new CustomEvent('product-price-starter:delta-suffix', {
          bubbles: true,
          detail: { suffix: windowDeltaSuffix, wrapperType: wrapperType },
        })
      );
    }

    priceEl.textContent = `${currencySymbol}${line}`;

    if (pricePrefix && hidePrefix) {
      pricePrefix.classList.add('d-hidden');
    }

    if (priceWrapper.classList.contains('loading-state')) {
      priceWrapper.classList.remove('loading-state');
    }
  }
}

if (!customElements.get('product-price-starter')) {
  customElements.define('product-price-starter', ProductPriceStarter);
}

ProductPriceStarter.exposeToWindow = function () {
  window.ProductPriceStarter = ProductPriceStarter;

  // Coalesce multiple callers in the same frame (init + timers + observers often stack).
  let rafId = null;
  let pendingAmount;
  let pendingOpts;

  function flushPending() {
    rafId = null;
    ProductPriceStarter.dispatchUpdate(pendingAmount, pendingOpts);
  }

  function scheduleUpdate(amount, opts) {
    pendingAmount = amount;
    pendingOpts = opts;
    if (rafId == null) {
      rafId = requestAnimationFrame(flushPending);
    }
  }

  window.productPriceStarter = scheduleUpdate;
  window.syncProductPriceStarter = scheduleUpdate;
};

ProductPriceStarter.exposeToWindow();

